import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import {
  Rocket, UserPlus, TrendingUp, Award, Users, Zap, Target, Star,
  ArrowRight, BookOpen, ShoppingBag, GraduationCap, Share2, Headphones,
  Trophy, ShieldAlert, Handshake, Sparkles, CheckCircle2,
} from "lucide-react";

/**
 * Smart Opportunity — content sourced verbatim from
 * SamaraWiz_About_Us_and_SmartOpportunity_Website_Content.docx
 * Sections (per the developer brief):
 *   1. Hero banner
 *   2. Sales and Marketing Strategy (4 pillars)
 *   3. Who Can Join
 *   4. How to Get Associated (6 steps)
 *   5. The SmartOpportunity Journey (7 items)
 *   6. Incentives, Growth & Recognition
 *   7. Important Disclosure
 *   8. Registration CTA
 */
export default function SmartOpportunity() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* ============ 1 · HERO ============ */}
      <section className="relative bg-royal-deep text-white overflow-hidden">
        <img
          src="/product-images/home-banner.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-royal-deep/95 via-royal/70 to-navy/90" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="text-[11px] uppercase tracking-[0.4em] text-gold mb-4">
            Smart Opportunity
          </div>
          <div className="font-serif text-2xl text-white/85 mb-3 italic">
            Learn. Share. Serve. Grow.
          </div>
          <h1
            data-testid="opp-hero-title"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-4xl"
          >
            Turn Product Knowledge and Customer Service into a
            <br />
            <span className="text-gold">Smart Business Opportunity.</span>
          </h1>
          <p className="mt-6 text-white/85 max-w-3xl leading-relaxed">
            SmartOpportunity is the Samara Wiz sales and marketing platform for
            individuals who want to use quality products, serve customers, develop
            practical business skills and build an associate network. The
            opportunity is supported by products, training, sales tools, incentives
            and recognition defined under the official company plan.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href="#associated"
              data-testid="opp-cta-how"
              className="bg-gold text-royal px-6 py-3 rounded-sm hover:bg-white transition-colors inline-flex items-center gap-2 font-medium"
            >
              How It Works <ArrowRight size={16} />
            </a>
            <Link
              to="/plan"
              data-testid="opp-cta-plan"
              className="border border-white/60 text-white px-6 py-3 rounded-sm hover:bg-white hover:text-royal transition-colors inline-flex items-center gap-2"
            >
              View the Compensation Plan
            </Link>
            <Link
              to="/register"
              data-testid="opp-cta-register"
              className="border border-gold text-gold px-6 py-3 rounded-sm hover:bg-gold hover:text-royal transition-colors inline-flex items-center gap-2"
            >
              <UserPlus size={16} /> Register as an Associate
            </Link>
          </div>
          <div className="mt-10 flex flex-wrap gap-2">
            {["strategy", "join", "associated", "journey", "incentive", "rewards"].map((k) => (
              <a
                key={k}
                href={`#${k}`}
                data-testid={`opp-tab-${k}`}
                className="text-[11px] px-3 py-1.5 border border-white/30 hover:bg-white hover:text-royal-deep rounded-sm uppercase tracking-widest transition-colors"
              >
                {k === "strategy"
                  ? "Strategy"
                  : k === "join"
                  ? "Who Can Join"
                  : k === "associated"
                  ? "Get Associated"
                  : k === "journey"
                  ? "Journey"
                  : k === "incentive"
                  ? "Incentives"
                  : "Recognition"}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ============ 2 · SALES & MARKETING STRATEGY ============ */}
      <section
        id="strategy"
        data-testid="opp-strategy"
        className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 scroll-mt-24"
      >
        <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-3">
          01 · Sales &amp; Marketing Strategy
        </div>
        <h2 className="font-serif text-3xl sm:text-4xl text-royal leading-tight max-w-3xl">
          Right guidance. Powerful products. A supportive network.
        </h2>
        <p className="text-royal/75 mt-4 max-w-3xl leading-relaxed">
          The Samara Wiz sales and marketing strategy is designed to help every
          associate build step by step with the right guidance, useful products and
          a supportive network. Associates begin by learning about the offerings,
          identifying customer needs, making responsible recommendations and
          providing dependable service.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
          {[
            { icon: Zap,    l: "Powerful Products",    d: "Useful, value-oriented products across everyday categories that customers actually need." },
            { icon: Users,  l: "Strong Network",       d: "Learn and grow with a team that supports consistent development at every stage." },
            { icon: Target, l: "Smart Marketing Tools", d: "Use product information, training and digital tools to reach customers professionally." },
            { icon: Rocket, l: "Unlimited Potential",  d: "Progress depends on eligible sales, sustained effort, customer service and team performance — earnings are not guaranteed." },
          ].map((c) => (
            <div
              key={c.l}
              data-testid={`strategy-${c.l.split(" ")[0].toLowerCase()}`}
              className="p-6 border border-line rounded-sm hover:border-gold/50 hover:shadow-md transition-all"
            >
              <div className="w-11 h-11 grid place-items-center bg-royal/[0.06] border border-royal/15 rounded-sm">
                <c.icon size={20} className="text-royal" />
              </div>
              <div className="font-serif text-lg text-royal mt-4 leading-snug">
                {c.l}
              </div>
              <p className="text-xs text-navy/65 mt-2 leading-relaxed">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ============ 3 · WHO CAN JOIN ============ */}
      <section
        id="join"
        data-testid="opp-join"
        className="bg-ivory py-20 border-y border-line scroll-mt-24"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-3">
              02 · Who Can Join?
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl text-royal leading-tight">
              Anyone with discipline, ethics and a customer-first mindset.
            </h2>
            <p className="text-royal/75 mt-6 leading-relaxed">
              SmartOpportunity may suit adults who are interested in product
              retailing, relationship-based sales, learning new skills and building
              a customer-focused business. No particular professional background is
              required, but success calls for discipline, ethical conduct, product
              understanding and consistent effort.
            </p>
            <Link
              to="/register"
              data-testid="opp-join-cta"
              className="mt-6 inline-flex items-center gap-2 bg-royal text-white px-6 py-3 rounded-sm hover:bg-royal-deep transition-colors font-medium"
            >
              Start Registration <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              "Discipline & Consistency",
              "Ethical Conduct",
              "Product Understanding",
              "Customer Focus",
              "Learning Mindset",
              "Team Spirit",
            ].map((t) => (
              <div
                key={t}
                className="p-4 bg-white border border-line rounded-sm hover:border-gold/40 transition-colors flex items-start gap-2"
              >
                <CheckCircle2 size={16} className="text-gold-deep shrink-0 mt-0.5" />
                <div className="text-sm text-royal/85">{t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ 4 · HOW TO GET ASSOCIATED ============ */}
      <section
        id="associated"
        data-testid="opp-associated"
        className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 scroll-mt-24"
      >
        <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-3">
          03 · How to Get Associated
        </div>
        <h2 className="font-serif text-3xl sm:text-4xl text-royal leading-tight max-w-3xl">
          A simple path to begin your journey — six steps.
        </h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            {
              n: "01",
              icon: ShoppingBag,
              l: "Buy and Experience",
              d: "Explore eligible Samara Wiz products and services. Understand their features, proper use, pricing and customer value before recommending them to others.",
            },
            {
              n: "02",
              icon: UserPlus,
              l: "Register",
              d: "Complete the official associate registration process, provide the required information, accept company policies and activate the account according to the current plan.",
            },
            {
              n: "03",
              icon: GraduationCap,
              l: "Learn",
              d: "Attend available product, sales and business training. Learn how Sales Points (SP), eligible sales, referrals, matching, payouts and customer support work.",
            },
            {
              n: "04",
              icon: Share2,
              l: "Share and Refer",
              d: "Share products responsibly with customers and refer interested individuals without making exaggerated product or income claims. Focus on genuine needs and long-term relationships.",
            },
            {
              n: "05",
              icon: Headphones,
              l: "Serve",
              d: "Follow up with customers, help them select appropriate products, resolve queries through authorized support channels and maintain professional records.",
            },
            {
              n: "06",
              icon: TrendingUp,
              l: "Earn and Grow",
              d: "Eligible earnings and rewards are calculated according to the official Samara Wiz compensation plan, applicable qualification rules and company policies.",
            },
          ].map((s) => (
            <div
              key={s.n}
              data-testid={`step-${s.n}`}
              className="p-6 bg-white border border-line rounded-sm hover:border-gold/50 hover:shadow-md transition-all relative"
            >
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 grid place-items-center bg-royal text-gold border border-royal/15 rounded-sm">
                  <s.icon size={20} />
                </div>
                <div className="font-serif text-3xl text-gold-deep/80">{s.n}</div>
              </div>
              <div className="font-serif text-xl text-royal mt-4">{s.l}</div>
              <p className="text-sm text-navy/65 mt-2 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
        <div className="mt-10">
          <Link
            to="/register"
            className="inline-flex items-center gap-2 bg-gold text-royal px-6 py-3 rounded-sm hover:bg-gold-deep hover:text-white transition-colors font-medium"
          >
            Register as an Associate <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* ============ 5 · THE JOURNEY ============ */}
      <section
        id="journey"
        data-testid="opp-journey"
        className="bg-royal text-white py-20 relative overflow-hidden scroll-mt-24"
      >
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-gold/15 rounded-full blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-3">
            04 · The SmartOpportunity Journey
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl leading-tight max-w-3xl">
            Seven milestones every associate walks — at their own pace.
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              "Use and understand",
              "Register and activate",
              "Learn and practise",
              "Share and sell responsibly",
              "Support customers",
              "Build and guide a team",
              "Qualify for applicable incentives and recognition",
            ].map((t, i) => (
              <div
                key={t}
                data-testid={`journey-${i + 1}`}
                className="p-5 bg-white/[0.06] border border-white/15 rounded-sm hover:bg-white/[0.10] hover:border-gold/40 transition-all flex items-start gap-3"
              >
                <div className="w-8 h-8 grid place-items-center bg-gold text-royal rounded-sm font-serif font-semibold shrink-0">
                  {i + 1}
                </div>
                <div className="text-sm text-white/90 leading-snug pt-1">{t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ 6 · INCENTIVES, GROWTH & RECOGNITION ============ */}
      <section
        id="incentive"
        data-testid="opp-incentive"
        className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 scroll-mt-24"
      >
        <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-3">
          05 · Incentives, Growth &amp; Recognition
        </div>
        <h2 className="font-serif text-3xl sm:text-4xl text-royal leading-tight max-w-3xl">
          Rewarding eligible effort and responsible leadership.
        </h2>

        {/* Sales Incentive + Retail Income */}
        <div className="grid md:grid-cols-2 gap-6 mt-10">
          <div
            data-testid="incentive-sales"
            className="p-8 border border-line rounded-sm hover:border-gold/40 transition-colors"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 grid place-items-center bg-gold text-royal rounded-sm">
                <TrendingUp size={20} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-gold">
                  Sales Incentive
                </div>
                <h3 className="font-serif text-xl text-royal">Sales Points (SP)</h3>
              </div>
            </div>
            <p className="text-royal/75 leading-relaxed">
              Samara Wiz recognizes eligible product sales through Sales Points (SP).
              Associates may qualify for the applicable sales incentive after meeting
              the required eligible SP and other conditions stated in the current
              compensation plan.
            </p>
            <ul className="mt-5 space-y-2">
              {[
                "Generate eligible Sales Points through qualifying product sales.",
                "Track progress using the official associate platform and company records.",
                "Meet the defined qualification and compliance conditions.",
                "Receive the applicable incentive through the approved payout process.",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2 text-sm text-royal/80">
                  <CheckCircle2 size={16} className="text-gold-deep shrink-0 mt-0.5" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>

          <div
            data-testid="incentive-retail"
            className="p-8 border border-line rounded-sm hover:border-gold/40 transition-colors"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 grid place-items-center bg-royal text-gold rounded-sm">
                <ShoppingBag size={20} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-gold">
                  Retail Income
                </div>
                <h3 className="font-serif text-xl text-royal">
                  AP → MRP margin
                </h3>
              </div>
            </div>
            <p className="text-royal/75 leading-relaxed">
              Associates may earn a retail margin when eligible products are
              purchased at the Associate Price (AP) and sold to customers at or
              within the Maximum Retail Price (MRP), subject to applicable law and
              company policy. Retail income is generally the difference between the
              customer selling price and the Associate Price.
            </p>
            <div className="mt-6 p-4 bg-ivory border border-dashed border-gold/40 rounded-sm text-xs text-royal/70 leading-relaxed">
              Retail margin is realised in real time on every eligible sale —
              independent of any team or matching income.
            </div>
          </div>
        </div>

        {/* Team-Based Growth */}
        <div
          id="team-growth"
          data-testid="incentive-team"
          className="mt-6 p-8 bg-gradient-to-br from-royal to-royal-deep text-white rounded-sm border border-royal-deep"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 grid place-items-center bg-gold text-royal rounded-sm">
              <Users size={20} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-gold">
                Team-Based Growth
              </div>
              <h3 className="font-serif text-xl">
                Referral · Matching · Bonuses · Rank Rewards
              </h3>
            </div>
          </div>
          <p className="text-white/85 leading-relaxed max-w-4xl">
            As associates develop customer sales and support an active team, they
            may become eligible for additional benefits described in the official
            plan, including referral benefits, matching or pair income, monthly
            bonuses and rank-based rewards. Every component is subject to its own
            qualification criteria, limits and payout rules.
          </p>
        </div>
      </section>

      {/* ============ 7 · RECOGNITION & REWARDS ============ */}
      <section
        id="rewards"
        data-testid="opp-rewards"
        className="bg-ivory py-20 border-y border-line scroll-mt-24"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-3">
            06 · Recognition and Rewards
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl text-royal leading-tight max-w-3xl">
            Hard work, ethics and leadership — celebrated on every stage.
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                icon: Trophy,
                l: "Achievement Recognition",
                d: "Celebrate verified milestones and inspire others through responsible performance.",
              },
              {
                icon: Star,
                l: "Exciting Rewards",
                d: "Qualify for applicable rewards and benefits under the current rank plan.",
              },
              {
                icon: Award,
                l: "Leadership Opportunities",
                d: "Develop the ability to guide, train and support an associate team.",
              },
              {
                icon: Handshake,
                l: "Respect and Appreciation",
                d: "Recognize professionalism, customer service, teamwork and ethical growth.",
              },
            ].map((c) => (
              <div
                key={c.l}
                data-testid={`reward-${c.l.split(" ")[0].toLowerCase()}`}
                className="p-6 bg-white border border-line rounded-sm hover:border-gold/50 hover:shadow-md transition-all"
              >
                <div className="w-11 h-11 grid place-items-center bg-gold text-royal rounded-sm">
                  <c.icon size={20} />
                </div>
                <div className="font-serif text-lg text-royal mt-4 leading-snug">
                  {c.l}
                </div>
                <p className="text-xs text-navy/65 mt-2 leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ 8 · IMPORTANT DISCLOSURE ============ */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div
          data-testid="opp-disclosure"
          className="p-8 border-2 border-dashed border-rose/40 bg-rose/[0.03] rounded-sm"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 grid place-items-center bg-rose text-white rounded-sm shrink-0">
              <ShieldAlert size={22} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-rose mb-2">
                Important Disclosure
              </div>
              <h3 className="font-serif text-2xl text-royal mb-4 leading-snug">
                SmartOpportunity is a sales-based business opportunity — not a job,
                investment scheme or promise of fixed income.
              </h3>
              <p className="text-royal/80 leading-relaxed">
                Earnings vary and depend on eligible product sales, qualifications,
                personal effort, customer demand, team performance, compliance with
                company policies and other factors. Prospective associates should
                review the latest official compensation plan, terms, refund/return
                policies and legal disclosures before registering.
              </p>
              <p className="mt-4 text-royal/80 leading-relaxed">
                <strong>Product and income claims must always be truthful,
                authorized and supportable.</strong>
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/plan"
                  className="text-sm inline-flex items-center gap-1 text-royal hover:text-gold border-b border-royal hover:border-gold pb-0.5"
                >
                  Read the compensation plan <ArrowRight size={14} />
                </Link>
                <Link
                  to="/legal/terms"
                  className="text-sm inline-flex items-center gap-1 text-royal hover:text-gold border-b border-royal hover:border-gold pb-0.5"
                >
                  Terms &amp; Conditions <ArrowRight size={14} />
                </Link>
                <Link
                  to="/legal/refund"
                  className="text-sm inline-flex items-center gap-1 text-royal hover:text-gold border-b border-royal hover:border-gold pb-0.5"
                >
                  Refund / Return Policy <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ 9 · FINAL CTA ============ */}
      <section className="bg-navy text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-14 h-14 grid place-items-center bg-gold text-royal rounded-sm mx-auto">
            <Sparkles size={22} />
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl mt-6 leading-tight">
            Ready to Learn, Share, Serve and Grow?
          </h2>
          <p className="mt-4 text-white/75 max-w-2xl mx-auto">
            Register as a Samara Wiz Associate today — free to join, easy to start,
            and supported by structured training and a growing customer community.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/register"
              data-testid="opp-cta-register-final"
              className="bg-gold text-royal px-7 py-3.5 rounded-sm hover:bg-white transition-colors inline-flex items-center gap-2 font-medium"
            >
              <UserPlus size={16} /> Register as an Associate
            </Link>
            <Link
              to="/plan"
              className="border-2 border-white text-white px-7 py-3.5 rounded-sm hover:bg-white hover:text-navy transition-colors inline-flex items-center gap-2"
            >
              <BookOpen size={16} /> View Official Plan
            </Link>
            <Link
              to="/contact"
              className="border-2 border-gold text-gold px-7 py-3.5 rounded-sm hover:bg-gold hover:text-royal transition-colors inline-flex items-center gap-2"
            >
              <Headphones size={16} /> Customer Support
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
